/**
 * GA4推奨形式の view_item イベント
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?sjid=1228647551205989991-NC&hl=ja&client_type=gtm#view_item
 */
import {
  ProductDetailColorSelectionEvent,
  ProductDetailViewEvent,
  ProductDetailViewEventFku,
} from '@/usecases/event-bus-for-third-party/events/product-detail'
import { EcommerceItem } from './ecommerce-item'

export interface ViewItemEvent {
  event: 'view_item'
  ecommerce: {
    currency: 'JPY',
    value: number,
    items: EcommerceItem[]
  }
}

/**
 * 商品詳細画面閲覧イベント ProductDetailViewEvent から ViewItemEvent を生成
 * 初期表示されているSKUのみview_itemされたとみなす
 */
const createFromProductDetailViewEvent = (event: ProductDetailViewEvent): ViewItemEvent => {
  const items = event.fku.reduce(
    (acc: EcommerceItem[], fku: ProductDetailViewEventFku): EcommerceItem[] => [
      ...acc,
      ...fku.sku.filter(sku => sku.isInitialDisplayed).map(sku => ({
        item_id: sku.id,
        item_name: event.root.name,
        item_variant: fku.color,
        price: sku.price,
        quantity: 1,
      })),
    ],
    []
  )
  // GA4の仕様として `value` は全SKUの総計とする
  const value = items.reduce((sum, item) => sum + item.price, 0)

  return {
    event: 'view_item',
    ecommerce: {
      currency: 'JPY',
      value,
      items,
    },
  }
}

/**
 * 商品詳細画面カラー選択変更イベント ProductDetailColorSelectionEvent から ViewItemEvent を生成
 */
const createFromProductDetailColorSelectionEvent = (event: ProductDetailColorSelectionEvent): ViewItemEvent => {
  return {
    event: 'view_item',
    ecommerce: {
      currency: 'JPY',
      value: event.sku.reduce((sum, sku) => sum + sku.price, 0),
      items: event.sku.map(sku => ({
        item_id: sku.id,
        item_name: event.productName,
        item_variant: event.colorName,
        price: sku.price,
        quantity: 1,
      })),
    },
  }
}

export const viewItemEventFactory = {
  createFromProductDetailViewEvent,
  createFromProductDetailColorSelectionEvent,
} as const
